import { Button } from 'components';
import { TableSkeleton } from 'components/Skeleton';
import { TableColumnType } from 'components/Table';
import { AppContext } from 'context/ContextProvider';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildTableRow } from 'views/Portfolio/helpers';
import {
  DisableSortBid,
  InitialiseMarketTable,
  InitialiseMyOffersTable,
  LoadingComponent,
  SortBidsNotification,
  applyFilters,
  generateRowsFormat,
  priceRangeFilters,
  sortOptions,
  subTabsBidsAndOffers,
} from 'views/Trade/helpers';
import { useSilentFetch } from 'views/Trade/hooks/useSilentFetch';
import { fetchPortfolioOffers } from 'views/Trade/services/fetchPortfolioOffers';
import { fetchPortfolioStocks } from 'views/Trade/services/fetchPortfolioStocks';
import {
  DisplayTextKeys,
  FilterConfigType,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  NoResultsTextKeys,
  IRowData,
  MARKET_REFS,
  OffersType,
  PortfolioOffersType,
  QUERY_KEYS,
  SUB_TABS_ENUMS,
  SetSlideoutConfigType,
  SlideoutConfigType,
  TABLE_ACTIONS,
} from 'views/Trade/types';
import { buildDisplayText, capitalizeFirstLetter, sortItems } from 'utils';
import ExpandingSearch from 'components/ExpandingSearch';
import { useFetch } from 'views/Trade/hooks/useFetch';
import { useIsProspect } from 'hooks/useIsProspect';
import { useSnackbar } from 'notistack';
import { fetchActiveMarketsFilters } from 'views/Trade/services/fetchActiveMarketsFilters';
import ServerSideTable from 'components/Table/ServerSideTable';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ViewStateType } from 'components/ProductTemplates/types';
import { fetchOffers } from 'views/Trade/services/fetchOffers';
import { fetchNumberOfStocksAgainstBids } from 'views/Trade/services/fetchNumberOfStocksAgainstBids';
import noResultImage from '../../../../assets/images/no_results_image.png';
import FilterView, { FiltersType, FilterViewRef } from '../SlideOutView/FilterView';
import { fetchActiveMarkets } from '../../services/fetchActiveMarkets1';

interface BidsAndOffersParams {
  slideOutConfig: SlideoutConfigType;
  setSlideOutConfig: SetSlideoutConfigType;
}

const staticPageSize = 50;
const PAGE_SIZE = 50;

const BidsAndOffers = ({ slideOutConfig, setSlideOutConfig }: BidsAndOffersParams) => {
  const {
    state: {
      settings: { accountInfo },
    },
    gbpToTargetCurrency,
  } = useContext(AppContext);

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const lwin18Search = searchParams.get('lwin18');
  const initSubTab = location.state?.initSubTab ?? SUB_TABS_ENUMS.MARKET;
  const initSortKey = location.state?.initSortKey ?? '';
  const initSortDesc = location.state?.initSortDesc ?? false;
  const { t } = useTranslation();
  const isProspect = useIsProspect();

  const filterViewRef = useRef<FilterViewRef>(null);

  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'trade:common', t), [t]);

  const [searchText, setSearchText] = useState<string>('');

  const [currentSubTab, setCurrentSubTab] = useState<string>(initSubTab);

  const [tablePagination, setTablePagination] = useState({
    page: 1,
  });
  const [filterSlideOutConfig, setFilterSlideOutConfig] = useState<FilterConfigType>({
    filters: {} as FiltersType<unknown> | null,
    onBack: () => {},
    isFiltered: false,
  });
  const [tableSortConfig, setTableSortConfig] = useState({
    sortKey: initSortKey,
    isAsc: !initSortDesc,
    sortOrder: initSortDesc ? 'desc' : 'asc',
  });

  const { enqueueSnackbar } = useSnackbar();

  const fetchStocksSetup = useMemo(() => {
    return {
      fetchPortfolioStocksSetup: {
        pageSize: PAGE_SIZE,
        queryFn: fetchPortfolioStocks,
        params: {},
        queryKey: QUERY_KEYS.FETCH_PORTFOLIO_STOCK,
        timeOutBetweenFetch: 4000,
      },

      fetchPortfolioOffersSetup: {
        pageSize: PAGE_SIZE,
        queryFn: fetchPortfolioOffers,
        params: {},
        queryKey: QUERY_KEYS.FETCH_PORTFOLIO_OFFERS,
        timeOutBetweenFetch: 3000,
      },
    };
  }, []);

  const {
    response: portfolioStocks,
    isActive: isActivePortfolioStocks,
    initIsLoading: isLoadingPortfolioStocks,
  } = useSilentFetch(fetchStocksSetup.fetchPortfolioStocksSetup);

  const {
    response: portfolioOffers,
    initIsLoading: isLoadingPortfolioOffers,
    count: portfolioOffersCount,
  } = useSilentFetch(fetchStocksSetup.fetchPortfolioOffersSetup);

  const { responce: activeMarketFiltersData } = useFetch({
    refetchOnWindowFocus: false,
    shouldCache: false,
    isEnabled: currentSubTab === SUB_TABS_ENUMS.MARKET,
    queryFn: () => fetchActiveMarketsFilters({ queryStr: searchText }, filterSlideOutConfig),
    queryKey: [QUERY_KEYS.FETCH_OFFERS_SEARCH_FILTERS, searchText, JSON.stringify(filterSlideOutConfig?.filters)],
  });

  const { responce: numberOfStocksAgainstBids, isLoading: isLoadingNumberOfStocksAgainstBids } = useFetch({
    refetchOnWindowFocus: false,
    shouldCache: false,
    isEnabled: currentSubTab === SUB_TABS_ENUMS.MARKET,
    queryFn: () => fetchNumberOfStocksAgainstBids(),
    queryKey: [QUERY_KEYS.FETCH_NUMBER_OF_STOCKS_AGAINST_BIDS, currentSubTab],
  });

  const { isLoading: loadingMarketSearch, responce: marketSearchData } = useFetch({
    refetchOnWindowFocus: false,
    shouldCache: false,
    isEnabled: currentSubTab === SUB_TABS_ENUMS.MARKET,
    queryFn: () =>
      fetchActiveMarkets(
        {
          queryStr: searchText,
          page: 0,
          pageSize: 50,
          sortFilter: tableSortConfig.sortKey,
          sortOrder: tableSortConfig.sortOrder,
        },
        filterSlideOutConfig,
      ),
    queryKey: [
      QUERY_KEYS.FETCH_OFFERS_SEARCH,
      searchText,
      JSON.stringify(filterSlideOutConfig?.filters),
      JSON.stringify(tableSortConfig),
    ],
  });

  const { isLoading: loadingPortfolioOfferSearch, responce: portfolioOfferSearchData } = useFetch({
    refetchOnWindowFocus: false,
    shouldCache: false,
    isEnabled: searchText.length > 0 && currentSubTab === SUB_TABS_ENUMS.MY_OFFERS,
    queryFn: () => fetchPortfolioOffers({ search: searchText, page: 0, pageSize: PAGE_SIZE }),
    queryKey: [QUERY_KEYS.FETCH_PORTFOLIO_OFFERS_SEARCH, searchText],
  });

  const searchMarketOffers = useMemo(() => {
    if (!marketSearchData?.data || loadingMarketSearch) return [];
    return marketSearchData.data || [];
  }, [marketSearchData?.data, loadingMarketSearch]);

  const searchPortfolioOffer = useMemo(() => {
    if (!portfolioOfferSearchData?.data || loadingPortfolioOfferSearch) return [];
    return portfolioOfferSearchData?.data || [];
  }, [portfolioOfferSearchData?.data, loadingPortfolioOfferSearch]);

  const { myOffersColumns, marketColumns } = useMemo(() => {
    return {
      ...InitialiseMyOffersTable(t, slideOutConfig, setSlideOutConfig, gbpToTargetCurrency, searchText),
      ...InitialiseMarketTable(t, slideOutConfig, setSlideOutConfig, gbpToTargetCurrency, searchText),
    };
  }, [slideOutConfig, setSlideOutConfig, t, gbpToTargetCurrency, searchText]);

  useEffect(() => {
    if (currentSubTab) filterViewRef.current?.resetFilters?.();
  }, [currentSubTab]);

  useEffect(() => {
    const navToProduct = async (text: string) => {
      try {
        const { data, count } = await fetchOffers({ searchText: text, page: 0, pageSize: 5 });

        if (!count) {
          enqueueSnackbar('Failed to find product');
          return;
        }
        const fxRate = accountInfo?.account.fxRate || 1;
        const { dataSource } = generateRowsFormat({
          fxRate,
          currentSubTab,
          offers: data,
          portfolioOffers: [],
          portfolioStocks: [],
          isProspect,
        });
        const rows = (dataSource || []).map((row) => {
          return {
            ...buildTableRow(
              {
                ...row,
              },
              marketColumns,
            ),
          };
        });

        const row = rows[0].rowData as unknown as IRowData;
        const {
          lwin18,
          wineName,
          quantityOutstanding: quantity,
          vintage,
          marketValueInTargetCurrency: marketValue,
          unitSize,
        } = row;
        setSlideOutConfig({
          searchText: '',
          innerViewState: ViewStateType.DEFAULT,
          view: ViewStateType.DEFAULT,
          isOpen: true,
          slidoutView: TABLE_ACTIONS.PURCHASE,
          offer: row,
          title: t(`trade:common.actions.${TABLE_ACTIONS.PURCHASE}`),
          wineInfoTemplateConfig: {
            lwin18,
            marketValue,
            quantity,
            unitSize,
            vintage,
            wineName,
            cultWinesAllocationRegion: row?.cultWinesAllocationRegion ?? '',
            region: row?.region,
          },
        });
      } catch (error) {
        enqueueSnackbar('Failed to find product');
      } finally {
        searchParams.delete('lwin18');
      }
    };
    if (lwin18Search) {
      navToProduct(lwin18Search);
    }
  }, [
    lwin18Search,
    isProspect,
    marketColumns,
    accountInfo?.account,
    currentSubTab,
    t,
    setSlideOutConfig,
    searchParams,
    enqueueSnackbar,
  ]);

  const fetchMoreMarketData = useCallback(async () => {
    if (currentSubTab === SUB_TABS_ENUMS.MY_OFFERS) return [];
    const fxRate = accountInfo?.account.fxRate || 1;
    const isSearch = searchText.length > 0;

    let storePortfolioOffers: PortfolioOffersType[] = [];
    try {
      const { data } = await fetchActiveMarkets(
        { queryStr: searchText, page: tablePagination.page, pageSize: staticPageSize },
        filterSlideOutConfig,
      );

      if (isSearch && currentSubTab === SUB_TABS_ENUMS.MY_OFFERS) {
        storePortfolioOffers = searchPortfolioOffer;
      } else {
        storePortfolioOffers = portfolioOffers;
      }

      const { dataSource: mainDataSource } = generateRowsFormat({
        fxRate,
        currentSubTab,
        offers: data ?? [],
        portfolioOffers: storePortfolioOffers,
        portfolioStocks: portfolioStocks,
        isProspect,
      });

      const rows = (mainDataSource || []).map((data1) => {
        return {
          ...buildTableRow(
            {
              ...data1,
            },
            marketColumns,
          ),
        };
      });

      setTablePagination((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));

      return rows ?? [];
    } catch (error) {
      return [];
    }
  }, [
    marketColumns,
    isProspect,
    portfolioStocks,
    tablePagination.page,
    searchText,
    filterSlideOutConfig,
    searchPortfolioOffer,
    currentSubTab,
    accountInfo?.account,
    portfolioOffers,
  ]);

  const onTableSort = (sortKey = '', isAsc = true) => {
    const { sortValue } = sortOptions.find((item) => item.sortKey === sortKey) || {};
    setTableSortConfig((prev) => ({
      ...prev,
      isAsc: Boolean(isAsc),
      sortKey: sortValue ?? '',
      sortOrder: isAsc ? 'asc' : 'desc',
    }));
  };

  const tableConfig = useMemo(() => {
    const fxRate = accountInfo?.account.fxRate || 1;
    const isSearch = searchText.length > 0;

    let storeOffers: OffersType[] = [];
    let storePortfolioOffers: PortfolioOffersType[] = [];

    if (currentSubTab === SUB_TABS_ENUMS.MARKET) {
      storeOffers = searchMarketOffers;
    }

    if (isSearch && currentSubTab === SUB_TABS_ENUMS.MY_OFFERS) {
      storePortfolioOffers = searchPortfolioOffer;
    } else {
      storePortfolioOffers = portfolioOffers;
    }

    const { dataSource: mainDataSource } = generateRowsFormat({
      fxRate,
      currentSubTab,
      offers: storeOffers,
      portfolioOffers: storePortfolioOffers,
      portfolioStocks: portfolioStocks,
      isProspect,
    });

    let source = mainDataSource;
    let columns: TableColumnType[] = [];
    let filtersData: Record<string, unknown>[] = [];
    let isLoading = true;

    if (currentSubTab === SUB_TABS_ENUMS.MARKET) {
      columns = marketColumns;
      filtersData = [...(activeMarketFiltersData?.data || []), ...priceRangeFilters];
      isLoading = loadingMarketSearch;
    } else {
      columns = myOffersColumns;
      filtersData = mainDataSource;
      isLoading = (!isSearch && isLoadingPortfolioOffers) || (loadingPortfolioOfferSearch && isSearch);

      if (filterSlideOutConfig.isFiltered) {
        const filteredDataSource = applyFilters([...mainDataSource], filterSlideOutConfig.filters);
        source = filteredDataSource?.filteredDataSource || [];
        filtersData = filteredDataSource?.filteredDataSource || [];
      }

      source = sortItems(
        source as unknown as Record<string, string | number>[],
        tableSortConfig.isAsc,
        tableSortConfig.sortKey,
      ) as unknown as Record<string, unknown>[];
    }

    const rows = (source || []).map((data) => {
      return {
        ...buildTableRow(
          {
            ...data,
          },
          columns,
        ),
      };
    });

    return {
      rows,
      source,
      mainDataSource,
      columns,
      isLoading,
      filtersData,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableSortConfig.isAsc,
    tableSortConfig.sortKey,
    filterSlideOutConfig.isFiltered,
    filterSlideOutConfig.filters,
    portfolioStocks,
    isLoadingPortfolioStocks,
    portfolioOffers,
    isLoadingPortfolioOffers,
    currentSubTab,
    accountInfo?.account,
    searchText,
    marketSearchData,
    loadingMarketSearch,
    searchPortfolioOffer,
    loadingPortfolioOfferSearch,
  ]);

  useEffect(() => {
    const numberOfBids = numberOfStocksAgainstBids?.data?.[0].numberOfBids ?? 0;

    const timeout = setTimeout(() => {
      if (
        !isActivePortfolioStocks &&
        !isLoadingNumberOfStocksAgainstBids &&
        !loadingMarketSearch &&
        numberOfBids > 0 &&
        !searchText &&
        !filterSlideOutConfig.isFiltered
      ) {
        enqueueSnackbar('', {
          autoHideDuration: 4000,
          content: () => {
            return (
              <SortBidsNotification
                id={1}
                message={`${numberOfBids} ${t('trade:common.bids_found_against')}`}
                sortFunction={() => onTableSort('highestBidPrice', true)}
              />
            );
          },
        });
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [
    filterSlideOutConfig.isFiltered,
    searchText,
    loadingMarketSearch,
    numberOfStocksAgainstBids?.data,
    isLoadingNumberOfStocksAgainstBids,
    isActivePortfolioStocks,
    enqueueSnackbar,
    t,
  ]);

  const resetPagination = () => {
    setTablePagination((prev) => ({
      ...prev,
      page: prev.page + 1,
    }));
  };

  const onSearch = (search: string) => {
    setSlideOutConfig((prev) => ({ ...prev, searchText: search }));
    setSearchText(search);
    resetPagination();
  };

  return (
    <div className="flex flex-1 sm:p-5 bg-gray-50 dark:bg-slate-900 w-full flex-col">
      <div className="flex items-start gap-3 mb-4 justify-between mt-2 md:items-center px-4 py-2 md:px-0 md:py-0">
        <div className="flex gap-3 flex-1 flex-col items-start justify-center md:flex-row md:items-center md:justify-normal">
          <div className="flex flex-row items-center gap-2">
            <div className="flex gap-2 items-center">
              <div className="flex items-center text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
                {subTabsBidsAndOffers.map((tab, index) => (
                  <button
                    key={tab.id}
                    onClick={() => setCurrentSubTab(tab.tabName)}
                    className={`py-2 px-5 whitespace-nowrap font-medium transition-all ${
                      index < subTabsBidsAndOffers.length - 1 ? 'border-r border-gray-200 dark:border-slate-700' : ''
                    } ${
                      currentSubTab === tab.tabName
                        ? 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-inner'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    {t(`trade:common.${tab.tabName}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col flex-1  items-center ">
            <FilterView
              isServerSide={true}
              ref={filterViewRef}
              customFilters={{
                [MARKET_REFS.REGION]: {
                  label: t('trade:dropDownFilters.title.region'),
                  selectAllText: t('trade:dropDownFilters.value.region'),
                  selected: [],
                  previewSelected: [],
                },

                [MARKET_REFS.PRODUCER]: {
                  label: t('trade:dropDownFilters.title.producer'),
                  selectAllText: t('trade:dropDownFilters.value.producer'),
                  selected: [],
                  previewSelected: [],
                },

                [MARKET_REFS.VINTAGE]: {
                  label: t('trade:dropDownFilters.title.vintage'),
                  selectAllText: t('trade:dropDownFilters.value.vintage'),
                  selected: [],
                  previewSelected: [],
                  isNumerical: true,
                },

                [MARKET_REFS.UNITSIZE]: {
                  label: t('trade:dropDownFilters.title.size'),
                  selectAllText: t('trade:dropDownFilters.value.size'),
                  selected: [],
                  previewSelected: [],
                  isNumerical: true,
                },
                [MARKET_REFS.WA]: {
                  label: t('trade:dropDownFilters.title.WA'),
                  selected: [],
                  previewSelected: [],
                  isNumerical: true,
                },

                [MARKET_REFS.VINOUS]: {
                  label: t('trade:dropDownFilters.title.vinous'),
                  selected: [],
                  previewSelected: [],
                  isNumerical: true,
                },

                [MARKET_REFS.PRICE_RANGE]: {
                  label: t('trade:dropDownFilters.title.price'),
                  selectAllText: t('trade:dropDownFilters.value.price'),
                  selected: [],
                  previewSelected: [],
                  isAsc: true,
                  customSortOrder: ['Under £500', 'Under £1000', '£1k-£2k', '£2k-£5k', '£5k-£10k', '£10k+'],
                },
              }}
              sourceData={tableConfig.filtersData}
              setFilteredSource={(isFiltered, customFilters) => {
                setFilterSlideOutConfig((prev) => ({
                  ...prev,
                  isFiltered: isFiltered,
                  filters: customFilters,
                }));
                resetPagination();
              }}
            />
          </div>
        </div>

        <div className="flex gap-2 flex-col items-end md:flex-row md:items-center">
          {/* A prospect has no physical stock to offer, so the CTA would only
              lead to a dead end. Browsing the book stays open to them. */}
          {!isProspect && (
            <Button
              isDisable={isLoadingPortfolioStocks}
              onClick={() =>
                setSlideOutConfig({
                  isOpen: true,
                  slidoutView: TABLE_ACTIONS.CREATE_OFFER,
                  title: 'Create Offers',
                  wineInfoTemplateConfig: { showTemplate: false },
                })
              }
              className="text-sm font-medium bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 max-h-9 px-5 text-white rounded-xl shadow-md shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-lg disabled:opacity-50 disabled:shadow-none whitespace-nowrap transition-all"
            >
              {displayText[DisplayTextKeys.CREATE_OFFER]}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-row justify-between items-center px-4 mb-3 md:px-0">
        <LoadingComponent
          className="text-gray-600 dark:text-gray-400"
          currentSubTab={currentSubTab}
          isLoading={tableConfig.isLoading}
          loadingText={displayText[DisplayTextKeys.LOADING]}
          resultLength={currentSubTab === SUB_TABS_ENUMS.MARKET ? marketSearchData?.count ?? 0 : portfolioOffersCount}
          resultText={capitalizeFirstLetter(displayText[DisplayTextKeys.RESULTS])}
          liveText={displayText[DisplayTextKeys.LIVE]}
        />

        <div className="mr-[-4px]">
          <ExpandingSearch
            uniqueIdRef=""
            placeholder={displayText[DisplayTextKeys.SEARCH]}
            data={[]}
            onSearch={(_, search) => onSearch(search || '')}
            className="focus:bg-white dark:focus:bg-slate-700 !max-h-9 rounded-xl"
            props={[]}
            debounce={800}
          />
        </div>
      </div>

      <div className="justify-center items-center bg-white dark:bg-slate-800 rounded-2xl flex-1 h-full shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700 overflow-hidden">
        {tableConfig.isLoading && (
          <div className="p-4">
            <TableSkeleton rows={8} columns={6} />
          </div>
        )}

        <div
          className={`overflow-hidden ${!tableConfig.isLoading && tableConfig.rows.length > 0 ? 'visible' : 'hidden'}`}
        >
          <ServerSideTable
            totalRows={currentSubTab === SUB_TABS_ENUMS.MARKET ? marketSearchData?.count : portfolioOffersCount}
            disableSort={false}
            disableSortFunc={() =>
              enqueueSnackbar('', { content: <DisableSortBid id={2} message={t('trade:common.sort_is_disabled')} /> })
            }
            dataSource={tableConfig.mainDataSource}
            onTablePagination={fetchMoreMarketData}
            onTableSort={onTableSort}
            columns={tableConfig.columns}
            rows={tableConfig.rows}
            applyfadeOnScrollClassName={false}
            isFadeOnScrollDisable={true}
          />
        </div>

        {!tableConfig.isLoading && !tableConfig.rows.length && (
          <div className="animate-[fade-in_5s_ease-out] p-8">
            <div className="bg-gray-50 dark:bg-slate-700/30 rounded-2xl p-8 text-center max-w-md mx-auto">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                <img className="w-12 h-12 object-contain" src={noResultImage} alt={t('common:noResult.title')} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('common:noResult.title')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('common:noResult.subTitle', 'Try adjusting your filters or search terms')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BidsAndOffers;
